import { InputHeader } from "@amzn/stencil-react-components/input";
import { Col } from "@amzn/stencil-react-components/layout";
import { Select } from "@amzn/stencil-react-components/select";
import { Text } from "@amzn/stencil-react-components/text";
import React, { FC } from "react";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import { describePickupBuilding, filterPickupBuildings } from "../delivery-choices.mapper";
import type { PickupBuilding } from "../choose-delivery.types";
import { CHOOSE_DELIVERY_STRINGS } from "../choose-delivery.constants";

const BUILDING_SELECT_ID = "pickup-building";

interface NoMatchOption {
  label: string;
  isNoMatch: true;
}

type BuildingOption = PickupBuilding | NoMatchOption;

const isNoMatch = (option: BuildingOption): option is NoMatchOption => "isNoMatch" in option;

const BuildingOptionContent: FC<{ building: PickupBuilding }> = ({ building }) => {
  const description = describePickupBuilding(building);
  return (
    <Col gridGap="S100">
      <Text fontSize="T200">{building.label}</Text>
      {description && (
        <Text fontSize="T100" color="neutral70">
          {description}
        </Text>
      )}
    </Col>
  );
};

export interface BuildingSelectProps {
  buildings: PickupBuilding[];
  selectedBuilding?: PickupBuilding;
  onSelect: (building: PickupBuilding) => void;
}

export const BuildingSelect: FC<BuildingSelectProps> = ({
  buildings,
  selectedBuilding,
  onSelect,
}) => {
  const filterOptions = (query: string): BuildingOption[] => {
    const matches = filterPickupBuildings(buildings, query);
    if (matches.length > 0) return matches;
    return [
      {
        label: getString(`${CHOOSE_DELIVERY_STRINGS}.buildingNoMatch`, { query: query.trim() }),
        isNoMatch: true,
      },
    ];
  };

  return (
    <Col gridGap="S200" width="100%">
      <InputHeader
        labelText={getString(`${CHOOSE_DELIVERY_STRINGS}.buildingLabel`)}
        htmlFor={BUILDING_SELECT_ID}
        required
      />
      <Select
        id={BUILDING_SELECT_ID}
        dataTestId={BUILDING_SELECT_ID}
        isSearchable
        isMulti={false}
        width="100%"
        options={buildings}
        value={selectedBuilding}
        valueAccessor="label"
        placeholder={getString(`${CHOOSE_DELIVERY_STRINGS}.buildingPlaceholder`)}
        searchFieldAriaLabel={getString(`${CHOOSE_DELIVERY_STRINGS}.buildingSearchLabel`)}
        filterOptions={filterOptions}
        renderOption={(option: BuildingOption) =>
          isNoMatch(option) ? (
            <Text fontSize="T200" color="neutral70" data-testid="building-no-match">
              {option.label}
            </Text>
          ) : (
            <BuildingOptionContent building={option} />
          )
        }
        onChange={(option?: BuildingOption) => {
          if (option && !isNoMatch(option)) onSelect(option);
        }}
      />
    </Col>
  );
};
